import React from 'react';

import { Button, CSSObject, MantineTheme, Menu, useMantineTheme } from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import KiloTooltip from 'components/KiloTooltip';
import useCancelAthletePackage from 'hooks/athletePackage/useCancelAthletePackage';
import useChangeAthletePackage from 'hooks/athletePackage/useChangeAthletePackage';
import useDeleteAthletePackage from 'hooks/athletePackage/useDeleteAthletePackage';
import useRenewAthletePackage from 'hooks/athletePackage/useRenewAthletePackage';
import useUndoCancelAthletePackage from 'hooks/athletePackage/useUndoCancelAthletePackage';
import useUpdateAthletePackage from 'hooks/athletePackage/useUpdateAthletePackage';
import { AthletePackageRetrieve, AthletePackageUpdate } from 'types';
import notificationHandler from 'utils/notifications';

import AutoPayModal from './components/AutoPayModal';
import AutoRenewModal from './components/AutoRenewModal';
import CancelModal from './components/CancelModal';
import ChangeAthleteStatusesModal from './components/ChangeAthleteStatusesModal';
import ChangeModal from './components/ChangeModal';
import ChangePaymentMethodModal from './components/ChangePaymentMethodModal';
import CreateAthletePackageHoldModal from './components/CreateAthletePackageHoldModal';
import DeleteModal from './components/DeleteModal';
import UndoCancelModal from './components/UndoCancelModal';
import { getPauseUnavailableReason } from './pauseAvailability';

const menuStyles = (t: MantineTheme): Record<string, CSSObject> => ({
  dropdown: {
    border: `1px solid ${t.colorScheme === 'dark' ? t.colors.dark[4] : t.colors.gray[2]}`,
    padding: '6px',
  },
  label: {
    fontSize: t.fontSizes.xs,
    fontWeight: 700 as const,
    letterSpacing: '0.06em',
    textTransform: 'uppercase' as const,
    color: t.colorScheme === 'dark' ? t.colors.dark[3] : t.colors.gray[5],
    paddingTop: '10px',
    paddingBottom: '4px',
    paddingLeft: '8px',
  },
  item: {
    fontSize: t.fontSizes.sm,
    fontWeight: 500 as const,
    borderRadius: t.radius.sm,
    padding: '8px 10px',
    '&[data-disabled]': {
      opacity: 0.4,
    },
  },
  divider: {
    marginTop: '4px',
    marginBottom: '2px',
    borderColor: t.colorScheme === 'dark' ? t.colors.dark[5] : t.colors.gray[1],
  },
});

interface ActionsMenuProps {
  athletePackage: AthletePackageRetrieve;
  disabled?: boolean;
  closeDrawer: () => void;
}

const ActionsMenu: React.FC<ActionsMenuProps> = ({ athletePackage, disabled = false, closeDrawer }) => {
  const theme = useMantineTheme();
  const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.sm}`, false);

  const updateAthletePackageMutation = useUpdateAthletePackage();
  const renewAthletePackageMutation = useRenewAthletePackage();
  const cancelAthletePackageMutation = useCancelAthletePackage(athletePackage.athletePackageId);
  const changeAthletePackageMutation = useChangeAthletePackage(athletePackage.athletePackageId);
  const undoCancelAthletePackageMutation = useUndoCancelAthletePackage();
  const deleteAthletePackageMutation = useDeleteAthletePackage();

  const { errorNotification, successNotification } = notificationHandler();

  const [isAutoPayModalOpen, { open: openAutoPay, close: closeAutoPay }] = useDisclosure(false);
  const [isChangePaymentMethodOpen, { open: openChangePaymentMethod, close: closeChangePaymentMethod }] =
    useDisclosure(false);
  const [
    isChangeAthleteStatusesOpen,
    { open: openChangeAthleteStatuses, close: closeChangeAthleteStatuses },
  ] = useDisclosure(false);
  const [
    isCreateAthletePackageHoldOpen,
    { open: openCreateAthletePackageHold, close: closeCreateAthletePackageHold },
  ] = useDisclosure(false);
  const [isCancelModalOpen, { open: openCancel, close: closeCancel }] = useDisclosure(false);
  const [isChangeModalOpen, { open: openChange, close: closeChange }] = useDisclosure(false);
  const [isDeleteModalOpen, { open: openDelete, close: closeDelete }] = useDisclosure(false);
  const [isAutoRenewModalOpen, { open: openAutoRenew, close: closeAutoRenew }] = useDisclosure(false);
  const [isUndoCancelModalOpen, { open: openUndoCancel, close: closeUndoCancel }] = useDisclosure(false);

  const handleToggleAutoRenew = (): void => {
    const payload: AthletePackageUpdate = {
      action: 'auto_renew',
      athletePackageId: athletePackage.athletePackageId,
      autoRenew: !athletePackage.autoRenew,
      autoPay: athletePackage.autoPay,
      paymentMethod: athletePackage.paymentMethod,
      nonStripePaymentMethod: athletePackage.nonStripePaymentMethod,
      athleteStatusOnStart: athletePackage.athleteStatusOnStart,
      athleteStatusOnEnd: athletePackage.athleteStatusOnEnd,
    };
    updateAthletePackageMutation.mutate(payload, {
      onError: error => {
        errorNotification('Athlete package update failed', error.response?.data.message);
      },
      onSuccess: () => {
        successNotification('Athlete package update success', `Athlete package successfully updated.`);
      },
    });
  };

  const handleRenew = (): void => {
    renewAthletePackageMutation.mutate(athletePackage.athletePackageId, {
      onError: error => {
        errorNotification('Athlete package renew failed', error.response?.data.message);
      },
      onSuccess: () => {
        successNotification('Athlete package renew success', `Athlete package successfully renewed.`);
      },
    });
  };

  const loadingDisable =
    updateAthletePackageMutation.isLoading ||
    cancelAthletePackageMutation.isLoading ||
    changeAthletePackageMutation.isLoading ||
    deleteAthletePackageMutation.isLoading ||
    renewAthletePackageMutation.isLoading ||
    undoCancelAthletePackageMutation.isLoading;

  const isActiveStatus = ['ACTIVE', 'HOLD', 'UPCOMING'].includes(athletePackage.status);

  const isOneTimePurchase = !!athletePackage.package.oneTimePurchase;
  const disableAutoRenew =
    loadingDisable ||
    !isActiveStatus ||
    !!athletePackage.cancellationDate ||
    !!athletePackage.package.maxSalesAllowed;
  const disableEnableAutoRenew = disableAutoRenew || isOneTimePurchase;
  const disableRenew = loadingDisable || athletePackage.isRenewDisabled || isOneTimePurchase;
  const disableAutoPayOn = loadingDisable || !isActiveStatus || athletePackage.paymentMethod === null;
  const disableAutoPayOff = loadingDisable || !isActiveStatus;
  const disableChangePaymentMethod = loadingDisable || !isActiveStatus;
  const disableChangeAthleteStatuses = loadingDisable || !isActiveStatus;
  const pauseUnavailableReason = getPauseUnavailableReason(athletePackage);
  const disableHolds = loadingDisable || !!pauseUnavailableReason;
  const disableCancel = loadingDisable || !isActiveStatus || !!athletePackage.cancellationDate;

  const disableChange =
    loadingDisable ||
    !isActiveStatus ||
    athletePackage.status === 'UPCOMING' ||
    !!athletePackage.cancellationDate;
  const disableDelete = loadingDisable || !isActiveStatus;
  const disableUndoCancel = loadingDisable || !isActiveStatus || !athletePackage.cancellationDate;

  return (
    <>
      <Menu shadow='lg' position='bottom-end' radius='md' width={230} styles={menuStyles}>
        <Menu.Target>
          <Button
            compact
            ml='auto'
            mr={0}
            mt={`calc(${theme.fontSizes.xl} * -.75)`}
            sx={{ alignSelf: 'center' }}
            disabled={disabled}
            data-cy='athletePackageDetailAction'
          >
            Manage package
          </Button>
        </Menu.Target>

        <Menu.Dropdown>
          <Menu.Label>Subscription</Menu.Label>
          {athletePackage.autoRenew ? (
            <Menu.Item disabled={disableAutoRenew} onClick={openAutoRenew}>
              Disable Auto-Renew
            </Menu.Item>
          ) : (
            <Menu.Item disabled={disableEnableAutoRenew} onClick={handleToggleAutoRenew}>
              Enable Auto-Renew
            </Menu.Item>
          )}
          <Menu.Item disabled={disableRenew} onClick={handleRenew}>
            Renew Package
          </Menu.Item>

          <Menu.Divider />

          <Menu.Label>Billing</Menu.Label>
          {athletePackage.autoPay ? (
            <Menu.Item disabled={disableAutoPayOff} onClick={openAutoPay}>
              Disable Auto-Pay
            </Menu.Item>
          ) : (
            <Menu.Item disabled={disableAutoPayOn} onClick={openAutoPay}>
              Enable Auto-Pay
            </Menu.Item>
          )}
          <Menu.Item disabled={disableChangePaymentMethod} onClick={openChangePaymentMethod}>
            Payment Method
          </Menu.Item>

          <Menu.Divider />

          <Menu.Label>Status</Menu.Label>
          <Menu.Item disabled={disableChangeAthleteStatuses} onClick={openChangeAthleteStatuses}>
            Manage Status Changes
          </Menu.Item>
          <KiloTooltip
            label={pauseUnavailableReason ?? ''}
            multiline
            width={220}
            withinPortal
            disabled={!pauseUnavailableReason}
          >
            <Menu.Item
              disabled={disableHolds}
              onClick={openCreateAthletePackageHold}
              data-cy='createAthletePackageHoldModal'
            >
              Pause Package
            </Menu.Item>
          </KiloTooltip>

          <Menu.Divider />

          <Menu.Label>Package Changes</Menu.Label>
          <Menu.Item disabled={disableChange} onClick={openChange} data-cy='athletePackageChangeItem'>
            Change Package
          </Menu.Item>
          {athletePackage.cancellationDate ? (
            <Menu.Item disabled={disableUndoCancel} onClick={openUndoCancel}>
              Undo Future Cancellation
            </Menu.Item>
          ) : (
            <Menu.Item disabled={disableCancel} onClick={openCancel}>
              Cancel Package
            </Menu.Item>
          )}
          <Menu.Item
            color='red'
            disabled={disableDelete}
            onClick={openDelete}
            data-cy='athletePackageDeleteItem'
          >
            Remove Package
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>

      <AutoPayModal
        opened={isAutoPayModalOpen}
        athletePackage={athletePackage}
        closeHandler={closeAutoPay}
        updateAthletePackageMutation={updateAthletePackageMutation}
      />
      <ChangePaymentMethodModal
        opened={isChangePaymentMethodOpen}
        athletePackage={athletePackage}
        closeHandler={closeChangePaymentMethod}
        updateAthletePackageMutation={updateAthletePackageMutation}
      />
      <ChangeAthleteStatusesModal
        opened={isChangeAthleteStatusesOpen}
        athletePackage={athletePackage}
        closeHandler={closeChangeAthleteStatuses}
        updateAthletePackageMutation={updateAthletePackageMutation}
      />
      <CreateAthletePackageHoldModal
        opened={isCreateAthletePackageHoldOpen}
        athletePackage={athletePackage}
        closeHandler={closeCreateAthletePackageHold}
        isDesktop={isDesktop}
      />
      <CancelModal
        opened={isCancelModalOpen}
        athletePackage={athletePackage}
        closeHandler={closeCancel}
        cancelAthletePackageMutation={cancelAthletePackageMutation}
        isDesktop={isDesktop}
      />
      <ChangeModal
        opened={isChangeModalOpen}
        athletePackage={athletePackage}
        closeHandler={closeChange}
        changeAthletePackageMutation={changeAthletePackageMutation}
        isDesktop={isDesktop}
      />
      <UndoCancelModal
        opened={isUndoCancelModalOpen}
        athletePackage={athletePackage}
        closeHandler={closeUndoCancel}
        undoCancelAthletePackageMutation={undoCancelAthletePackageMutation}
      />
      <DeleteModal
        opened={isDeleteModalOpen}
        athletePackage={athletePackage}
        closeModal={closeDelete}
        closeDrawer={closeDrawer}
        deleteAthletePackageMutation={deleteAthletePackageMutation}
      />
      <AutoRenewModal
        athletePackage={athletePackage}
        closeHandler={closeAutoRenew}
        updateAthletePackageMutation={updateAthletePackageMutation}
        isDesktop={isDesktop}
        opened={isAutoRenewModalOpen}
      />
    </>
  );
};

export default ActionsMenu;
